import { JobTitle } from '@pflegia/data-access';
import { B_PRIORITIES, C_PRIORITIES, CandidatePriority } from '@pflegia/types/api/candidate/candidate-priority';
import {
  PreRecognitionQuestion,
  QUALIFICATION_TO_RECOGNITION,
  QualificationRecognition,
  RecognitionQuestions,
} from '@pflegia/types/api/candidate/recognition';
import { CareType } from '@pflegia/types/api/employer/care-type';
import { FunnelQuestion, FunnelQuestionCategory } from '@pflegia/types/api/funnel/questions';
import { ConfirmationStatus } from '@pflegia/types/api/general';
import { JobTitleEnum } from '@pflegia/types/api/job-title/job-titles.enum';
import { ErrorType, PrioritizationError } from '@pflegia/types/api/job-title/multiposting/errors';
import { DowngradeFn, JobTitleCategory, RequiredJobTitle } from '@pflegia/types/api/job-title/multiposting/types';
import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { RecognitionStatus } from '@pflegia/types/common/candidate/recognition-status';
import { ConditionalStep, LocalFunnelStep } from '@pflegia/ui/funnel/types';
import { getSelectedJobTitle } from '@pflegia/ui/funnel/utils/job-titles';
import { FunnelAnswers } from '@pflegia/ui/pflegia/funnel/types/funnel-answers';
import { assertIsDefined } from '@pflegia/util/functions/type-guards';
import { getMainPriorityJobTitle } from '@pflegia/util/pflegia/job-title';
import { isTherapistJobTitle } from '@pflegia/util/pflegia/job-title/is-therapist-job-title';

import { skipRecognitionFlow } from '../helpers/multiposting/skip-recognition-flow';

type RecognitionStep = LocalFunnelStep<JobTitle, FunnelAnswers, RecognitionQuestions, { sourceDomain: SourceDomain }>;
type RecognitionDowngradeFn<Q> = DowngradeFn<Q, { isPraxia: boolean; employerCareType?: CareType }>;

export const qualificationRecognitionStep: ConditionalStep<RecognitionStep, { previousStep: PreRecognitionQuestion }> = (
  { isMultiposting, previousStep } = { previousStep: FunnelQuestion.JobQualification, isMultiposting: true },
) => ({
  category: FunnelQuestionCategory.EducationAndTraining,
  condition: answers => {
    if (answers[previousStep] !== ConfirmationStatus.AcquiredAbroad) return false;
    if (!isMultiposting) return true;
    const selectedJobTitles = answers[FunnelQuestion.PositionDesired]?.map(({ jobTitle }) => jobTitle) as RequiredJobTitle[];
    const mainPriorityJobTitle = getMainPriorityJobTitle(selectedJobTitles);

    return !skipRecognitionFlow(mainPriorityJobTitle);
  },
  key: QUALIFICATION_TO_RECOGNITION[previousStep],
  getNewJobTitle: isMultiposting ? getMultipostingDowngrade : getRegularFunnelDowngrade(previousStep),
});

const getRegularFunnelDowngrade =
  (previousStep: PreRecognitionQuestion): RecognitionStep['getNewJobTitle'] =>
  (answer, answers, allJobTitles, props) => {
    // Skip downgrade For DQualification
    if (previousStep === FunnelQuestion.DQualification) return null;

    if (answer.status !== RecognitionStatus.NotYetApplied) {
      return null;
    }

    const selectedJobTitle = getSelectedJobTitle<JobTitle>(answers, previousStep);
    const typedProps = props as { sourceDomain: SourceDomain } | undefined;
    const downgradedJobTitle = getDowngradedJobTitle(selectedJobTitle, typedProps?.sourceDomain === SourceDomain.PraxiaDe);

    if (selectedJobTitle.title === downgradedJobTitle) return null;

    const nextJobTitle = allJobTitles.find(jt => jt.title === downgradedJobTitle);
    assertIsDefined(nextJobTitle, 'Career changers');

    return nextJobTitle;
  };

const getDowngradedJobTitle = (currentJobTitle: JobTitle, isPraxia: boolean) => {
  const trackingPriority = currentJobTitle.trackingPriority as CandidatePriority;
  const { title } = currentJobTitle;

  if (B_PRIORITIES.includes(trackingPriority)) {
    if (title === JobTitleEnum.ElderlyCareNurse) return JobTitleEnum.AssistantElderlyCareNurse;

    if (trackingPriority === CandidatePriority.B) {
      return JobTitleEnum.AssistantNurse;
    }

    if (isTherapistJobTitle(currentJobTitle as RequiredJobTitle)) {
      return isPraxia ? JobTitleEnum.MedicalAssistance : JobTitleEnum.AssistantNurse;
    }

    return JobTitleEnum.MedicalAssistance;
  }

  if (C_PRIORITIES.includes(trackingPriority)) {
    switch (title) {
      case JobTitleEnum.Paramedic:
        return JobTitleEnum.AssistantNurse;
      case JobTitleEnum.RemedialCareNurse:
        return JobTitleEnum.AssistantRemedialCareNurse;
    }
  }
  return title;
};

const getMultipostingDowngrade: RecognitionStep['getNewJobTitle'] = (answer, answers, jobTitles, props) => {
  const selectedJobTitles = answers[FunnelQuestion.PositionDesired]?.map(({ jobTitle }) => jobTitle) as RequiredJobTitle[];
  assertIsDefined(selectedJobTitles, 'Selected job titles');

  const mainPriorityJobTitle = getMainPriorityJobTitle(selectedJobTitles);
  assertIsDefined(mainPriorityJobTitle, 'Main priority job title');

  const typedProps = props as { sourceDomain: SourceDomain } | undefined;
  const isPraxia = typedProps?.sourceDomain === SourceDomain.PraxiaDe;
  // @ts-expect-error MP funnel will pass jobPosition to the props
  const employerCareType = props?.jobPosition?.employer?.careType;

  const downgradedJobTitles = getDowngradedJobTitleForMultiposting(mainPriorityJobTitle, answer, { isPraxia, employerCareType });
  if (!downgradedJobTitles?.length) return null;

  if (downgradedJobTitles.some(jt => !jobTitles.find(jobTitle => jobTitle.title === jt))) {
    return null;
  }

  const newJobTitles = downgradedJobTitles.map(downgradedJt => {
    const newJobTitle = jobTitles.find(jobTitle => jobTitle.title === downgradedJt);
    assertIsDefined(newJobTitle, 'Downgraded job title');
    return newJobTitle;
  });
  return newJobTitles;
};

const getDowngradedJobTitleForMultiposting: RecognitionDowngradeFn<QualificationRecognition> = (mainPriorityJobTitle, answer, props) => {
  const parentPriority = mainPriorityJobTitle.jobTitle?.trackingPriority[0];
  switch (parentPriority) {
    case CandidatePriority.A:
      return getDowngradePathForA(mainPriorityJobTitle, answer, props);
    case CandidatePriority.B:
      return getDowngradePathForB(mainPriorityJobTitle, answer, props);
    case CandidatePriority.C:
      return getDowngradePathForC(mainPriorityJobTitle, answer, props);
  }
  return null;
};

const getDowngradePathForA: RecognitionDowngradeFn<QualificationRecognition> = (
  mainPriorityJobTitle,
  answer,
  { isPraxia, employerCareType },
) => {
  if (
    mainPriorityJobTitle.jobTitle.title === JobTitleEnum.PracticeManager &&
    (answer.status === RecognitionStatus.Completed || answer.status === RecognitionStatus.InProgress)
  ) {
    if (employerCareType === CareType.Physiotherapy) return [JobTitleEnum.PhysicalTherapist];
    if (employerCareType === CareType.DentalPractice) return [JobTitleEnum.QualifiedDentalEmployee];
    return [JobTitleEnum.MedicalAssistant];
  }

  if (mainPriorityJobTitle.category === JobTitleCategory.Praxia)
    return answer.status === RecognitionStatus.NotYetApplied
      ? [JobTitleEnum.MedicalAssistance]
      : [JobTitleEnum.MedicalAssistant, JobTitleEnum.QualifiedDentalEmployee];

  if (answer.status === RecognitionStatus.NotYetApplied)
    return isPraxia
      ? [JobTitleEnum.MedicalAssistance, JobTitleEnum.QualifiedDentalEmployee]
      : [JobTitleEnum.AssistantNurse, JobTitleEnum.AssistantElderlyCareNurse];

  return isPraxia
    ? [JobTitleEnum.MedicalAssistance, JobTitleEnum.QualifiedDentalEmployee]
    : [JobTitleEnum.Nurse, JobTitleEnum.ElderlyCareNurse];
};

const getDowngradePathForB: RecognitionDowngradeFn<QualificationRecognition> = (
  mainPriorityJobTitle,
  answer,
  { isPraxia, employerCareType },
) => {
  if (answer.status !== RecognitionStatus.NotYetApplied) return null;
  const { trackingPriority } = mainPriorityJobTitle.jobTitle;
  switch (trackingPriority) {
    case CandidatePriority.B:
      if (employerCareType === CareType.Physiotherapy) return [JobTitleEnum.PhysicalTherapist];
      return isPraxia ? [JobTitleEnum.MedicalAssistance] : [JobTitleEnum.AssistantNurse, JobTitleEnum.AssistantElderlyCareNurse];
    case CandidatePriority.B2:
    case CandidatePriority.B3:
    case CandidatePriority.B4:
    case CandidatePriority.B5:
    case CandidatePriority.B6:
      return [JobTitleEnum.MedicalAssistance];
  }
  throw new PrioritizationError(ErrorType.UnknownPriority, mainPriorityJobTitle);
};

const getDowngradePathForC: RecognitionDowngradeFn<QualificationRecognition> = (_mainPriorityJobTitle, answer, { isPraxia }) => {
  if (answer.status === RecognitionStatus.NotYetApplied) {
    return isPraxia ? [JobTitleEnum.MedicalAssistance, JobTitleEnum.QualifiedDentalEmployee] : [JobTitleEnum.AssistantNurse];
  }
  return null;
};
